import { useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";

/**
 * BeforeAfterSlider — dual-image comparison slider.
 * Drag the vertical handle to reveal the "before" image (left) over the "after" (right).
 *
 * Props:
 *   - before, after: image URLs
 *   - labelBefore, labelAfter: short caption for each side (e.g., "Chiuso", "Aperto")
 *   - alt: accessible description
 */
export default function BeforeAfterSlider({
  before,
  after,
  labelBefore = "",
  labelAfter = "",
  alt = "",
}) {
  const [pos, setPos] = useState(50); // percentage 0-100
  const ref = useRef(null);
  const dragging = useRef(false);

  const updateFromClientX = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const raw = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, raw)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    updateFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    updateFromClientX(e.clientX);
  };
  const onPointerUp = () => {
    dragging.current = false;
  };

  return (
    <div
      ref={ref}
      className="relative w-full aspect-video rounded-2xl overflow-hidden select-none bg-black touch-none"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      data-testid="before-after-slider"
    >
      {/* AFTER image (bottom layer, always fully visible) */}
      <img
        src={after}
        alt={alt}
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      />
      {/* BEFORE image (top layer, clipped to `pos`%) */}
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{ width: `${pos}%` }}
      >
        <img
          src={before}
          alt={alt}
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ width: ref.current ? `${ref.current.getBoundingClientRect().width}px` : "100%", maxWidth: "none" }}
        />
      </div>

      {/* Divider + handle */}
      <div
        className="absolute inset-y-0 w-[2px] bg-white/85 shadow-[0_0_20px_rgba(255,255,255,0.35)] pointer-events-none"
        style={{ left: `calc(${pos}% - 1px)` }}
      />
      <motion.div
        className="absolute top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white text-[#0d1b2a] flex items-center justify-center shadow-xl cursor-ew-resize pointer-events-none"
        style={{ left: `calc(${pos}% - 22px)` }}
        animate={{ scale: dragging.current ? 1.08 : 1 }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" transform="translate(-3 0)" />
          <polyline points="9 18 15 12 9 6" transform="translate(3 0)" />
        </svg>
      </motion.div>
    </div>
  );
}
